import type { ComponentType } from "react";
import { View } from "tamagui";
import { getTokenValue } from "../../tamagui-exports";
import type { SizeTokensKeys } from "../../themes/token-size";
import { BrandGraphicIndicator, type BrandGraphicIndicatorKind } from "./BrandGraphicIndicator";
import { BillGraphic } from "./graphics/BillGraphic";
import { ClaimCheckGraphic } from "./graphics/ClaimCheckGraphic";
import { CoverageGraphic } from "./graphics/CoverageGraphic";
import { DocumentGraphic } from "./graphics/DocumentGraphic";
export type BrandGraphicType = "claim-check" | "bill" | "coverage" | "document";
export type BrandGraphicSize = `$${SizeTokensKeys}`;
export type BrandGraphicProps = {
  type: BrandGraphicType;
  /** Optional green-circle overlay. Omit for no indicator. */
  indicator?: BrandGraphicIndicatorKind;
  /** Size token the whole graphic scales to. Defaults to `$si12` (96px). */
  size?: BrandGraphicSize;
};

// The graphics are authored at this size; everything scales from it.
const DESIGN_SIZE = 160;
const GRAPHICS: Record<BrandGraphicType, ComponentType> = {
  "claim-check": ClaimCheckGraphic,
  bill: BillGraphic,
  coverage: CoverageGraphic,
  document: DocumentGraphic
};

// Indicator placement per type, in the 160×160 design space: circle center (cx,
// cy) and diameter. Cards pin the circle to the top-right corner; the coverage
// shield carries it lower on its right edge.
const INDICATOR_PLACEMENT: Record<BrandGraphicType, {
  cx: number;
  cy: number;
  diameter: number;
}> = {
  "claim-check": {
    cx: 114,
    cy: 8,
    diameter: 48
  },
  bill: {
    cx: 114,
    cy: 8,
    diameter: 48
  },
  coverage: {
    cx: 119,
    cy: 115,
    diameter: 48
  },
  document: {
    cx: 134,
    cy: 14,
    diameter: 48
  }
};
export const BrandGraphic = ({
  type,
  indicator,
  size = "$si12"
}: BrandGraphicProps) => {
  const px = getTokenValue(size, "size");
  const scale = px / DESIGN_SIZE;
  const Graphic = GRAPHICS[type];
  const placement = INDICATOR_PLACEMENT[type];
  return <View width={px} height={px} position="relative" testID={`brand-graphic-${type}`} data-sentry-element="View" data-sentry-component="BrandGraphic" data-sentry-source-file="BrandGraphic.tsx">
      <View position="absolute" top={(px - DESIGN_SIZE) / 2} left={(px - DESIGN_SIZE) / 2} width={DESIGN_SIZE} height={DESIGN_SIZE} scale={scale} data-sentry-element="View" data-sentry-source-file="BrandGraphic.tsx">
        <Graphic data-sentry-element="Graphic" data-sentry-source-file="BrandGraphic.tsx" />
        {indicator ? <BrandGraphicIndicator kind={indicator} diameter={placement.diameter} left={placement.cx - placement.diameter / 2} top={placement.cy - placement.diameter / 2} /> : null}
      </View>
    </View>;
};